import type React from 'react'
import { PenNodeEdit } from '@/components/app/editor/tools/shapes-tool'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { SideNavItem } from '../constants/tool-rail-items'

interface ToolRailButtonProps {
  item: SideNavItem
  isActive: boolean
  /** Swap the icon for the pen± node-edit glyph (Select button while in node-edit mode). */
  showNodeEditIcon: boolean
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Controlled tooltip state; `undefined` leaves the tooltip uncontrolled. */
  tooltipOpen?: boolean
  onTooltipOpenChange?: (open: boolean) => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  onClick: (id: string) => void
}

/** One tool icon in the left rail, with its tooltip. */
export function ToolRailButton({
  item,
  isActive,
  showNodeEditIcon,
  buttonRef,
  tooltipOpen,
  onTooltipOpenChange,
  onPointerEnter,
  onPointerLeave,
  onClick,
}: ToolRailButtonProps) {
  const { Icon } = item

  return (
    <Tooltip open={tooltipOpen} onOpenChange={onTooltipOpenChange}>
      <TooltipTrigger asChild>
        <Button
          ref={buttonRef}
          variant="ghost"
          size="icon-sm"
          onClick={() => onClick(item.id)}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          className={cn(
            'text-foreground hover:bg-accent hover:text-accent-foreground',
            isActive && 'bg-accent text-accent-foreground'
          )}
          aria-label={item.label}
        >
          {showNodeEditIcon ? (
            <PenNodeEdit className={cn('w-4 h-4 text-current', isActive && 'font-semibold')} />
          ) : (
            <Icon className={cn('text-current', isActive && 'font-semibold')} />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  )
}
